import { Try } from "functype";
import { Brand } from "functype/branded";
import { z } from "zod";
// Simple Brand Factories
export const UserId = (id) => Brand("UserId", id);
export const TaskId = (id) => Brand("TaskId", id);
export const ListId = (id) => Brand("ListId", id);
export const ProviderId = (id) => Brand("ProviderId", id);
export const ExternalId = (id) => Brand("ExternalId", id);
export const AccessToken = (token) => Brand("AccessToken", token);
export const RefreshToken = (token) => Brand("RefreshToken", token);
export const EncryptedToken = (token) => Brand("EncryptedToken", token);
export const ApiKey = (key) => Brand("ApiKey", key);
export const EncryptionKey = (key) => Brand("EncryptionKey", key);
export const StripeCustomerId = (id) => Brand("StripeCustomerId", id);
export const StripeSubscriptionId = (id) => Brand("StripeSubscriptionId", id);
export const StripePaymentMethodId = (id) => Brand("StripePaymentMethodId", id);
export const StripeInvoiceId = (id) => Brand("StripeInvoiceId", id);
export const OAuthState = (state) => Brand("OAuthState", state);
export const OAuthCode = (code) => Brand("OAuthCode", code);
export const CodeChallenge = (challenge) => Brand("CodeChallenge", challenge);
export const RedirectUri = (uri) => Brand("RedirectUri", uri);
// Validation functions
export const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
export const isValidUrl = (s) => Try(() => new URL(s)).isSuccess();
export const isValidHttpsUrl = (s) => Try(() => new URL(s))
    .map((u) => u.protocol === "https:")
    .fold(() => false, (v) => v);
export const isValidConnectionString = (s) => s.startsWith("postgres://") || s.startsWith("postgresql://") || s.startsWith("mysql://");
export const isValidPort = (n) => n >= 1 && n <= 65535;
export const isValidPercentage = (n) => n >= 0 && n <= 100;
export const isValidUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
export const isValidIpAddress = (s) => /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(s);
export const isValidEmailDomain = (s) => /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(s);
export const isValidApiKeySecret = (s) => s.length >= 32;
export const isValidWebhookSecret = (s) => s.length >= 32;
export const isValidJwtSecret = (s) => s.length >= 32;
export const isValidTaskTitle = (s) => s.trim().length > 0 && s.length <= 500;
export const isValidTaskDescription = (s) => s.length <= 10000;
export const isValidProviderName = (s) => /^[a-z][a-z0-9-]*[a-z0-9]$/.test(s) && s.length <= 50;
export const isValidTenantId = (s) => s.trim().length > 0;
export const isValidClientId = (s) => s.trim().length > 0;
// Safe constructors that validate
export const EmailAddress = (email) => {
    if (!isValidEmail(email)) {
        throw new Error(`Invalid email address: ${email}`);
    }
    return Brand("EmailAddress", email);
};
export const UUID = (uuid) => {
    if (!isValidUUID(uuid)) {
        throw new Error(`Invalid UUID: ${uuid}`);
    }
    return Brand("UUID", uuid);
};
// Legacy factory constructors for Url / HttpsUrl / ConnectionString / Port /
// Percentage / IpAddress / EmailDomain were removed — no callers across the
// monorepo. Consumers should use the zod schemas defined further down.
// Zod Integration Utilities
// Return types are inferred: zod 4 removed ZodEffects (transform/refine now
// build a ZodPipe), so we let TypeScript derive the schema type rather than
// annotate zod-4 internals that may shift again. z.infer on the result still
// resolves to BrandType<B, T> because that's the transform's output type.
export function zodBranded(brand, schema) {
    return schema.transform((val) => Brand(brand, val));
}
export function zodValidatedBrand(brand, schema, validator, errorMessage) {
    return schema
        .refine(validator, { message: errorMessage ?? `Invalid ${brand}` })
        .transform((val) => Brand(brand, val));
}
// Zod schemas with branded types
export const UserIdSchema = zodValidatedBrand("UserId", z.string(), isValidUUID, "Invalid user ID (must be UUID)");
export const TaskIdSchema = z.string().transform((val) => TaskId(val));
export const ListIdSchema = z.string().transform((val) => ListId(val));
export const ProviderIdSchema = z.string().transform((val) => ProviderId(val));
export const ExternalIdSchema = z.string().transform((val) => ExternalId(val));
export const EmailAddressSchema = zodValidatedBrand("EmailAddress", z.string(), isValidEmail, "Invalid email address");
export const UrlSchema = zodValidatedBrand("Url", z.string(), isValidUrl, "Invalid URL");
export const HttpsUrlSchema = zodValidatedBrand("HttpsUrl", z.string(), isValidHttpsUrl, "Invalid HTTPS URL");
export const ConnectionStringSchema = zodValidatedBrand("ConnectionString", z.string(), isValidConnectionString, "Invalid connection string");
export const PortSchema = zodValidatedBrand("Port", z.number(), isValidPort, "Port must be between 1 and 65535");
export const PercentageSchema = zodValidatedBrand("Percentage", z.number(), isValidPercentage, "Percentage must be between 0 and 100");
export const UUIDSchema = zodValidatedBrand("UUID", z.string(), isValidUUID, "Invalid UUID");
export const IpAddressSchema = zodValidatedBrand("IpAddress", z.string(), isValidIpAddress, "Invalid IP address");
export const EmailDomainSchema = zodValidatedBrand("EmailDomain", z.string(), isValidEmailDomain, "Invalid email domain");
// New branded type schemas
export const SupabaseUrlSchema = zodValidatedBrand("SupabaseUrl", z.string(), isValidHttpsUrl, "Invalid Supabase URL (must be HTTPS)");
export const ApiKeySecretSchema = zodValidatedBrand("ApiKeySecret", z.string(), isValidApiKeySecret, "API key must be at least 32 characters long");
export const WebhookSecretSchema = zodValidatedBrand("WebhookSecret", z.string(), isValidWebhookSecret, "Webhook secret must be at least 32 characters long");
export const DatabaseConnectionStringSchema = zodValidatedBrand("DatabaseConnectionString", z.string(), isValidConnectionString, "Invalid database connection string");
export const JwtSecretSchema = zodValidatedBrand("JwtSecret", z.string(), isValidJwtSecret, "JWT secret must be at least 32 characters long");
export const TaskTitleSchema = zodValidatedBrand("TaskTitle", z.string(), isValidTaskTitle, "Task title must be non-empty and max 500 characters");
export const TaskDescriptionSchema = zodValidatedBrand("TaskDescription", z.string(), isValidTaskDescription, "Task description must be max 10000 characters");
export const ProviderNameSchema = zodValidatedBrand("ProviderName", z.string(), isValidProviderName, "Provider name must be lowercase alphanumeric with hyphens, max 50 chars");
export const TenantIdSchema = zodValidatedBrand("TenantId", z.string(), isValidTenantId, "Tenant ID cannot be empty");
export const ClientIdSchema = zodValidatedBrand("ClientId", z.string(), isValidClientId, "Client ID cannot be empty");
// Type guards
export const isUserId = (val) => typeof val === "string" && isValidUUID(val);
export const isTaskId = (val) => typeof val === "string";
export const isListId = (val) => typeof val === "string";
export const isProviderId = (val) => typeof val === "string";
export const isEmailAddress = (val) => typeof val === "string" && isValidEmail(val);
export const isUrl = (val) => typeof val === "string" && isValidUrl(val);
export const isHttpsUrl = (val) => typeof val === "string" && isValidHttpsUrl(val);
export const isConnectionString = (val) => typeof val === "string" && isValidConnectionString(val);
export const isPort = (val) => typeof val === "number" && isValidPort(val);
export const isPercentage = (val) => typeof val === "number" && isValidPercentage(val);
export const isUUID = (val) => typeof val === "string" && isValidUUID(val);
export const isIpAddress = (val) => typeof val === "string" && isValidIpAddress(val);
export const isEmailDomain = (val) => typeof val === "string" && isValidEmailDomain(val);
// Type guards for the removed brand types (SupabaseUrl, ApiKeySecret, etc.) were
// also unused across the monorepo and have been removed. Consumers use the
// zod-validated brand schemas below instead.
//# sourceMappingURL=branded.js.map